import React, { useMemo, useState } from "react";
import { Calculator as CalcIcon, ArrowRight, Activity, Watch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KM_PER_MILE = 1.609344;

// Race distances for the "race result" mode (in meters)
const DISTANCES = [
  { id: "5k", label: "5K", meters: 5000 },
  { id: "10k", label: "10K", meters: 10000 },
  { id: "15k", label: "15K", meters: 15000 },
  { id: "10mile", label: "10 Mile", meters: 16093.4 },
  { id: "half", label: "Half-Marathon", meters: 21097.5 },
  { id: "marathon", label: "Marathon", meters: 42195 },
];

// Zones expressed as a fraction of THRESHOLD VELOCITY.
// pace_at_pct = threshold_pace / pct  (so pct < 1 = slower pace, pct > 1 = faster pace)
const ZONES = [
  { key: "very-easy", name: "Very easy", low: 0, high: 0.77, openBelow: true,
    blurb: "Very easy jogging." },
  { key: "easy", name: "Easy", low: 0.77, high: 0.82,
    blurb: "Conversational pace running." },
  { key: "steady", name: "Steady", low: 0.82, high: 0.88,
    blurb: "Aerobic running with purpose." },
  { key: "tempo", name: "Tempo", low: 0.88, high: 0.94,
    blurb: "Moderate and sustained running." },
  { key: "threshold", name: "Threshold", low: 0.97, high: 1.00,
    blurb: "Comfortably hard running." },
  { key: "vo2max", name: "VO₂ Max", low: 1.07, high: 1.15,
    blurb: "Hard efforts, close to maximum heart rate." },
  { key: "speed", name: "Speed", low: 1.15, high: null, openAbove: true,
    blurb: "Short & sharp repetitions." },
];

// ---------- Daniels VDOT helpers ----------

// Jack Daniels' oxygen-cost equation (VO₂ at a given velocity in m/min)
function vo2FromVelocity(v) {
  return -4.6 + 0.182258 * v + 0.000104 * v * v;
}

// Solve the Daniels quadratic for velocity given a target VO₂
function velocityFromVO2(targetVO2) {
  const a = 0.000104;
  const b = 0.182258;
  const c = -4.6 - targetVO2;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return 0;
  return (-b + Math.sqrt(disc)) / (2 * a);
}

// % of VO₂max sustainable for a given race duration (minutes)
function vo2maxFraction(timeMinutes) {
  return (
    0.8 +
    0.1894393 * Math.exp(-0.012778 * timeMinutes) +
    0.2989558 * Math.exp(-0.1932605 * timeMinutes)
  );
}

function vdotFromRace(meters, timeMinutes) {
  if (meters <= 0 || timeMinutes <= 0) return 0;
  const v = meters / timeMinutes; // m/min
  const vo2 = vo2FromVelocity(v);
  const fraction = vo2maxFraction(timeMinutes);
  return vo2 / fraction;
}

// Threshold velocity = velocity at 88.5% of VDOT
function thresholdVelocityFromVDOT(vdot) {
  return velocityFromVO2(vdot * 0.885);
}

// ---------- Pace formatting helpers ----------

function formatPace(minutesDecimal) {
  if (minutesDecimal == null || !isFinite(minutesDecimal) || minutesDecimal <= 0) return "—";
  const totalSec = Math.round(minutesDecimal * 60);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function paceFromVelocity(vMetersPerMin) {
  if (!vMetersPerMin || vMetersPerMin <= 0) return { perKm: null, perMile: null };
  const minPerKm = 1000 / vMetersPerMin;
  return { perKm: minPerKm, perMile: minPerKm * KM_PER_MILE };
}

// Parse "MM:SS" or "M:SS" pace string into decimal minutes
function parsePaceMMSS(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2}):(\d{1,2})$/);
  if (!m) return null;
  const minutes = parseInt(m[1], 10);
  const seconds = parseInt(m[2], 10);
  if (seconds >= 60) return null;
  return minutes + seconds / 60;
}

// ---------- Component ----------

export default function PaceCalculator() {
  const [mode, setMode] = useState("race"); // 'race' | 'threshold'

  // Race-result inputs
  const [distance, setDistance] = useState("10k");
  const [hh, setHH] = useState("");
  const [mm, setMM] = useState("45");
  const [ss, setSS] = useState("00");

  // Manual-threshold inputs
  const [thresholdInput, setThresholdInput] = useState("4:30");
  const [thresholdUnit, setThresholdUnit] = useState("km"); // 'km' | 'mi'

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onNumber = (setter, max = 2) => (e) => {
    const v = e.target.value.replace(/[^0-9]/g, "");
    setter(v.slice(0, max));
  };

  // Resolve threshold velocity (m/min) from whichever input mode is active
  const thresholdVelocity = useMemo(() => {
    if (!submitted) return 0;
    if (mode === "race") {
      const totalMin =
        (parseInt(hh || "0", 10) * 60) +
        parseInt(mm || "0", 10) +
        parseInt(ss || "0", 10) / 60;
      const meters = DISTANCES.find((d) => d.id === distance)?.meters || 0;
      if (totalMin <= 0 || meters <= 0) return 0;
      const vdot = vdotFromRace(meters, totalMin);
      return thresholdVelocityFromVDOT(vdot);
    }
    // threshold mode
    const paceMin = parsePaceMMSS(thresholdInput);
    if (!paceMin) return 0;
    // convert to min/km if input was per mile
    const paceMinPerKm = thresholdUnit === "mi" ? paceMin / KM_PER_MILE : paceMin;
    // velocity in m/min
    return 1000 / paceMinPerKm;
  }, [submitted, mode, distance, hh, mm, ss, thresholdInput, thresholdUnit]);

  const thresholdPaces = useMemo(() => paceFromVelocity(thresholdVelocity), [thresholdVelocity]);

  const rows = useMemo(() => {
    if (!thresholdVelocity) return [];
    return ZONES.map((z) => {
      // pace at a given pct of threshold velocity:
      //   v_pct = thresholdVelocity * pct  →  pace = 1000 / v_pct
      const paceAt = (pct) => paceFromVelocity(thresholdVelocity * pct);

      if (z.openBelow) {
        // anything slower than `high` percent
        const p = paceAt(z.high);
        return { ...z, kind: "slower", perKm: p.perKm, perMile: p.perMile };
      }
      if (z.openAbove) {
        const p = paceAt(z.low);
        return { ...z, kind: "faster", perKm: p.perKm, perMile: p.perMile };
      }
      const pHigh = paceAt(z.high); // fastest end of band
      const pLow = paceAt(z.low);   // slowest end of band
      return {
        ...z,
        kind: "range",
        perKmRange: [pLow.perKm, pHigh.perKm],   // [slow, fast]
        perMileRange: [pLow.perMile, pHigh.perMile],
      };
    });
  }, [thresholdVelocity]);

  const handleCalculate = (e) => {
    e.preventDefault();
    setError("");

    if (mode === "race") {
      const totalMin =
        (parseInt(hh || "0", 10) * 60) +
        parseInt(mm || "0", 10) +
        parseInt(ss || "0", 10) / 60;
      if (totalMin <= 0) {
        setError("Enter a race time greater than zero.");
        setSubmitted(false);
        return;
      }
      if (totalMin > 600) {
        setError("Race time looks unrealistic — please double check.");
        setSubmitted(false);
        return;
      }
    } else {
      const paceMin = parsePaceMMSS(thresholdInput);
      if (!paceMin || paceMin < 2 || paceMin > 20) {
        setError("Enter a threshold pace as MM:SS (e.g. 4:30).");
        setSubmitted(false);
        return;
      }
    }
    setSubmitted(true);
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative py-24 lg:py-32 border-b border-white/10 bg-[#080808]"
    >
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
          <div className="lg:col-span-3 flex items-start gap-4">
            <span className="font-mono text-brand-red text-xs tracking-widest">05</span>
            <span className="label-eyebrow">Pace Calculator</span>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-display uppercase text-white text-5xl lg:text-7xl leading-[0.9] tracking-tight max-w-3xl">
              Precision Running <span className="text-brand-red">pace calculator.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-white/65 text-base lg:text-lg leading-relaxed">
              Get your training zones from your{" "}
              <strong className="text-white">threshold pace</strong>. Enter your threshold
              pace directly, or estimate it from a recent race result. Any other questions, please{" "}
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="calc-contact-link"
                className="text-brand-red underline underline-offset-4 decoration-brand-red/40 hover:decoration-brand-red transition-colors"
              >
                reach out
              </a>
              .
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Input panel */}
          <form
            onSubmit={handleCalculate}
            data-testid="calculator-form"
            className="lg:col-span-4 bg-[#0e0e10] border border-white/10 p-6 lg:p-8 flex flex-col gap-6 self-start"
          >
            {/* Mode tabs */}
            <div
              role="tablist"
              data-testid="calc-mode-tabs"
              className="grid grid-cols-2 border border-white/10"
            >
              <ModeTab
                active={mode === "race"}
                onClick={() => {
                  setMode("race");
                  setSubmitted(false);
                }}
                icon={<Watch size={14} />}
                label="Race result"
                testId="calc-mode-race"
              />
              <ModeTab
                active={mode === "threshold"}
                onClick={() => {
                  setMode("threshold");
                  setSubmitted(false);
                }}
                icon={<Activity size={14} />}
                label="Threshold pace"
                testId="calc-mode-threshold"
              />
            </div>

            {mode === "race" ? (
              <>
                <div className="flex flex-col gap-2">
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">
                    Race distance
                  </Label>
                  <Select value={distance} onValueChange={setDistance}>
                    <SelectTrigger
                      data-testid="calc-select-distance"
                      className="bg-white/5 border border-white/10 rounded-none text-white h-12 focus:ring-0 focus:border-brand-red"
                    >
                      <SelectValue placeholder="Choose distance" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border border-white/10 rounded-none text-white">
                      {DISTANCES.map((d) => (
                        <SelectItem
                          key={d.id}
                          value={d.id}
                          data-testid={`calc-distance-${d.id}`}
                        >
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">
                    Race time
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <TimeBox label="HH" testid="calc-input-hh" value={hh} onChange={onNumber(setHH)} />
                    <TimeBox label="MM" testid="calc-input-mm" value={mm} onChange={onNumber(setMM)} />
                    <TimeBox label="SS" testid="calc-input-ss" value={ss} onChange={onNumber(setSS)} />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 mt-1">
                    Leave hours blank for sub-1-hour races.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">
                    Threshold pace
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="calc-input-threshold"
                      value={thresholdInput}
                      onChange={(e) => setThresholdInput(e.target.value)}
                      inputMode="numeric"
                      placeholder="MM:SS"
                      className="bg-white/5 border border-white/10 rounded-none text-white text-center font-display text-2xl tracking-wider h-14 focus-visible:border-brand-red focus-visible:ring-0 flex-1"
                    />
                    <div className="grid grid-cols-2 border border-white/10 w-32">
                      <UnitTab
                        active={thresholdUnit === "km"}
                        onClick={() => setThresholdUnit("km")}
                        label="/km"
                        testId="calc-unit-km"
                      />
                      <UnitTab
                        active={thresholdUnit === "mi"}
                        onClick={() => setThresholdUnit("mi")}
                        label="/mi"
                        testId="calc-unit-mi"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 mt-1">
                    Format: minutes:seconds — e.g. 4:30
                  </p>
                </div>
              </>
            )}

            {error && (
              <div
                data-testid="calc-error"
                className="text-xs text-brand-red border border-brand-red/40 bg-brand-red/10 px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="calc-submit"
              className="btn-primary mt-2"
            >
              Calculate zones <ArrowRight size={16} />
            </button>

            {thresholdVelocity > 0 && (
              <div
                data-testid="calc-threshold-summary"
                className="border-t border-white/10 pt-5 mt-2"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">
                  Threshold pace
                </div>
                <div className="mt-2 flex items-baseline gap-5 flex-wrap">
                  <div className="font-display text-3xl text-white leading-none">
                    {formatPace(thresholdPaces.perKm)}
                    <span className="text-white/40 text-xs font-sans font-normal ml-1">/km</span>
                  </div>
                  <div className="font-display text-3xl text-white leading-none">
                    {formatPace(thresholdPaces.perMile)}
                    <span className="text-white/40 text-xs font-sans font-normal ml-1">/mi</span>
                  </div>
                </div>
                {mode === "race" && (
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold pt-3 mt-3 border-t border-white/5">
                    <span>From race</span>
                    <span className="font-mono text-white/70 normal-case tracking-normal">
                      {DISTANCES.find((d) => d.id === distance)?.label} ·{" "}
                      {hh ? `${hh}:` : ""}
                      {mm.padStart(2, "0")}:{ss.padStart(2, "0")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </form>

          {/* Results */}
          <div className="lg:col-span-8 bg-[#0e0e10] border border-white/10">
            {!submitted || thresholdVelocity <= 0 ? (
              <div
                data-testid="calc-empty-state"
                className="p-10 lg:p-16 min-h-[420px] flex flex-col items-start justify-center"
              >
                <div className="font-display uppercase text-white/30 text-5xl lg:text-7xl leading-[0.9] tracking-tight">
                  Your zones<br />will appear here.
                </div>
                <p className="mt-6 text-white/45 max-w-md text-sm">
                  Choose a mode on the left and hit{" "}
                  <span className="text-brand-red">Calculate zones</span>.
                </p>
              </div>
            ) : (
              <div data-testid="calc-results-table" className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="p-4 lg:p-5 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold w-1/3">
                        Zone
                      </th>
                      <th className="p-4 lg:p-5 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">
                        Per km
                      </th>
                      <th className="p-4 lg:p-5 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">
                        Per mile
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.key}
                        data-testid={`calc-row-${r.key}`}
                        className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="p-4 lg:p-5 align-top">
                          <div>
                            <div className="font-display uppercase text-white text-xl lg:text-2xl tracking-wide">
                              {r.name}
                            </div>
                            <div className="text-xs text-white/50 leading-snug max-w-xs mt-1">
                              {r.blurb}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/30 font-mono mt-2">
                              {zonePctLabel(r)}
                            </div>
                          </div>
                        </td>
                        <PaceCell row={r} unit="perKm" suffix="/km" />
                        <PaceCell row={r} unit="perMile" suffix="/mi" />
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4 lg:p-5 text-[10px] uppercase tracking-[0.22em] text-white/35 font-mono border-t border-white/10">
                  Paces are starting estimates. Adjust for terrain, weather, sleep & fatigue.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- subcomponents ----------

function ModeTab({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center justify-center gap-2 px-3 py-3 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors ${
        active
          ? "bg-brand-red text-white"
          : "bg-transparent text-white/55 hover:text-white"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function UnitTab({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center justify-center text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
        active ? "bg-brand-red text-white" : "bg-transparent text-white/55 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function TimeBox({ label, value, onChange, testid }) {
  return (
    <div className="relative">
      <Input
        data-testid={testid}
        value={value}
        onChange={onChange}
        inputMode="numeric"
        placeholder="00"
        className="bg-white/5 border border-white/10 rounded-none text-white text-center font-display text-2xl tracking-wider h-14 focus-visible:border-brand-red focus-visible:ring-0"
      />
      <span className="absolute -bottom-4 left-0 right-0 text-center text-[9px] uppercase tracking-[0.25em] text-white/40 font-bold">
        {label}
      </span>
    </div>
  );
}

function PaceCell({ row, unit, suffix }) {
  // "slower" → single pace prefixed with "≥"
  // "faster" → single pace prefixed with "≤"
  // "range"  → "fastEnd – slowEnd"
  if (row.kind === "slower") {
    const pace = row[unit];
    return (
      <td className="p-4 lg:p-5 align-top">
        <div className="font-display text-2xl lg:text-3xl text-white tracking-wide">
          ≥ {formatPace(pace)}
          <span className="text-white/40 text-sm font-sans font-normal ml-1">{suffix}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-mono mt-1">
          Slower than {formatPace(pace)}
        </div>
      </td>
    );
  }
  if (row.kind === "faster") {
    const pace = row[unit];
    return (
      <td className="p-4 lg:p-5 align-top">
        <div className="font-display text-2xl lg:text-3xl text-white tracking-wide">
          ≤ {formatPace(pace)}
          <span className="text-white/40 text-sm font-sans font-normal ml-1">{suffix}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-mono mt-1">
          Faster than {formatPace(pace)}
        </div>
      </td>
    );
  }
  const key = unit === "perKm" ? "perKmRange" : "perMileRange";
  const [slow, fast] = row[key];
  return (
    <td className="p-4 lg:p-5 align-top">
      <div className="font-display text-2xl lg:text-3xl text-white tracking-wide">
        {formatPace(fast)} – {formatPace(slow)}
        <span className="text-white/40 text-sm font-sans font-normal ml-1">{suffix}</span>
      </div>
    </td>
  );
}

function zonePctLabel(row) {
  if (row.openBelow) return `< ${Math.round(row.high * 100)}% LT`;
  if (row.openAbove) return `> ${Math.round(row.low * 100)}% LT`;
  return `${Math.round(row.low * 100)}–${Math.round(row.high * 100)}% LT`;
}
